// PARTNER-HARANG — a hívó SAJÁT értesítés-feedje a portál-topbarban.
//
// A FELÉPÍTÉS A KÖZÖS VÁZBÓL jön (`features/notifications/components/notification-bell-base.tsx`): azonos
// panel-geometria, számláló-plakett, csontváz, hiba- és üres-állapot, a régebbi értesítések végtelen
// görgetése és a lista-footer. MIÉRT (user-lelet): itt korábban egy LESZŰKÍTETT másolat élt, és a
// legördülő „egyáltalán nem úgy nézett ki, mint az admin oldalon" — a hiányzó állapotok (töltés/hiba) pedig
// néma üres panelt adtak, ami hibánál HAZUGSÁG.
//
// AMI PARTNER-SPECIFIKUS és ezért itt dől el:
//  • a MÉLYLINK célja (`portalRefToRoute`) — a partner nem juthat belső route-ra,
//  • a SZÖVEGEK (a portál ÖNÖZ — `foundations/design.md` §8.4).
//
// A FEED SZÁNDÉKOSAN GLOBÁLIS (nem szűkül a fiók-váltóra): az értesítés a SZEMÉLYHEZ szól, nem a fiókhoz —
// egy elrejtett értesítés elveszett értesítés, és a partner nem is tudná, hogy szűrő van rajta. A tényt viszont
// kimondjuk (`scopeNote`), a mélylink pedig ÁTÁLLÍTJA a scope-ot a jegy fiókjára (különben olyan jegyre visz,
// ami a szűkített listán nem szerepel). A fiók NEVE már a szöveg része („A(z) XYZ fiók alatt található…"),
// ezért soronkénti fiók-jelvény NEM kell (user-döntés).
//
// AZ ÉLŐ FRISSÍTÉS VISZONT UGYANAZ: a partner is a hitelesített, SAJÁT SSE-csatornáját kapja
// (`useNotificationStream`) — a csatornát az Odin a TOKENBŐL képezi, a frame jelzés (nem adat), és
// per-identity stream-plafon fékezi. A korábbi 60 s-os REST-poll egyszerre lassabb visszajelzés ÉS több
// terhelés volt (lásd `notify-stream.controller.ts` `@AllowPartner()` indoklása).
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { type NotificationRow } from '@/features/notifications/api';
import { NotificationBellBase } from '@/features/notifications/components/notification-bell-base';
import { useNotificationFeed } from '@/features/notifications/hooks/use-notification-feed';
import { useNotificationStream } from '@/features/notifications/hooks/use-notification-stream';
import { usePortal } from '@/features/portal/portal-context';
import {
  portalRefToRoute,
  type PortalNotificationRoute,
} from '@/features/portal/portal-ref-to-route';

/** Az értesítés `data.accountId`-ja (a jegy-lánc viszi) — bármi más alak → `null`. */
function accountIdOf(row: NotificationRow): string | null {
  const value = row.data?.accountId;
  return typeof value === 'string' && value !== '' ? value : null;
}

export function PortalBell(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me, accountId, setAccountId } = usePortal();
  const accounts = me?.accounts ?? [];
  // A jelvény-szám a KÖZÖS feed-queryből (a váz is ezt olvassa — egy query, egy igazság); itt csak a
  // szám-interpolációhoz kell.
  const { unreadCount } = useNotificationFeed();
  // ÉLŐ csatorna (SSE) — ugyanaz a hook, mint a belső harangon: a jelzésre a feed-query invalidálódik. 429-en
  // (per-identity stream-plafon) magától REST-pollra esik vissza, tehát nincs külön portál-ág.
  useNotificationStream();

  return (
    <NotificationBellBase<PortalNotificationRoute>
      texts={{
        triggerTitle: t('portal.shell.notificationsTitle'),
        triggerAria: t('portal.shell.notifications', { count: 0 }),
        triggerAriaWithCount: t('portal.shell.notifications', { count: unreadCount }),
        panelTitle: t('portal.shell.notificationsTitle'),
        markAll: t('portal.shell.markAllRead'),
        emptyTitle: t('portal.shell.noNotifications'),
        emptyDescription: t('portal.shell.noNotificationsDesc'),
        errorTitle: t('portal.shell.notificationsErrorTitle'),
        errorDescription: t('portal.shell.notificationsErrorDesc'),
        loading: t('portal.shell.notificationsLoading'),
        liveCount: t('portal.shell.notifications', { count: unreadCount }),
        openList: t('portal.shell.openMessages'),
      }}
      resolveRoute={portalRefToRoute}
      navigateToRoute={(route, row) => {
        // KONTEXTUS-VÁLTÁS a mélylink ELŐTT: a harang feedje SZÁNDÉKOSAN globális (az értesítés a személyhez
        // szól, nem a fiókhoz — egy elrejtett értesítés elveszett értesítés), a LAPOK viszont a fiók-váltóra
        // szűkülnek. Ha tehát a partner egy MÁS fiók jegyére kattint, előbb átállítjuk a scope-ot arra a
        // fiókra — különben olyan jegyre érkezik, ami a szűkített listáján nem is szerepel („miért nem
        // találom?"). CSAK a SAJÁT fiókjaira váltunk (`me.accounts`): egy idegen/elavult id fantom-scope-ot
        // csinálna, amiben minden lista üres. Régi értesítés-sorokon nincs `accountId` → nincs váltás.
        const target = accountIdOf(row);
        if (target !== null && target !== accountId && accounts.some((a) => a.id === target)) {
          setAccountId(target);
        }
        void navigate(
          route.to === '/portal/messages'
            ? { to: route.to, search: route.search }
            : { to: route.to, params: route.params },
        );
      }}
      onOpenList={() => void navigate({ to: '/portal/messages' })}
      // SCOPE-JELZÉS NINCS: a harang továbbra is MINDEN fiók értesítését mutatja (a postafiók SZEMÉLYES —
      // lásd a fájl-fejlécet), de ezt nem írjuk ki jelvénnyel a panel fejlécébe (user-döntés —
      // a napközbeni ellenkező döntést váltja). A mélylink ATTÓL FÜGGETLENÜL átállítja a scope-ot.
    />
  );
}
